/**
 * Loading / error / no-counted-rows / content for one cached Keyword Database
 * block. A failed block renders only its error: no row from an earlier or
 * partial response is ever shown beside it.
 */
import type { UseQueryResult } from '@tanstack/react-query';
import { AlertTriangle } from 'lucide-react';
import type { KeywordDatabaseBlock } from '@a7y/types';
import { NoCountedRows } from './NoCountedRows';

export function BlockPanel<T>({
  label,
  query,
  children,
}: {
  label: string;
  query: UseQueryResult<KeywordDatabaseBlock<T>>;
  children: (body: T, block: KeywordDatabaseBlock<T>) => React.ReactNode;
}) {
  if (query.isError) {
    return (
      <div
        role="alert"
        className="flex items-center gap-2 rounded-lg border border-red-300 dark:border-red-700 bg-red-50 dark:bg-red-950/40 p-3 text-sm text-red-700 dark:text-red-300"
      >
        <AlertTriangle className="h-4 w-4 shrink-0" />
        {label} failed to load. Refresh retries it; the Worker logs name the failing query.
      </div>
    );
  }
  const block = query.data;
  if (!block) {
    return <div className="p-6 text-sm text-zinc-500">Loading {label}…</div>;
  }
  if (block.state === 'no-counted-rows') {
    return <NoCountedRows dataset={block.dataset} />;
  }
  return <>{children(block.body, block)}</>;
}
